import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { newsArticles } from '../data/newsData';
import PageLayout from '../components/PageLayout';
import { ArrowLeft, Calendar, Clock, Tag, ArrowRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';

export default function NewsDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = newsArticles.find(a => a.slug === slug);

  if (!article) {
    return (
      <PageLayout>
        <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px' }}>
          <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', marginBottom: '16px' }}>Article not found</h2>
          <button onClick={() => navigate('/news')} style={{ backgroundColor: '#F97316', color: 'white', padding: '12px 28px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: '600' }}>Back to News</button>
        </div>
      </PageLayout>
    );
  }

  const related = newsArticles.filter(a => a.slug !== slug && a.category === article.category).slice(0, 3);

  return (
    <PageLayout>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`${article.category}, tech blog, ${article.title}`}
        url={`/news/${article.slug}`}
        image={article.image}
        type="article"
      />
      {/* Hero */}
      <div style={{ background: '#F9FAFB', padding: '40px 24px 48px' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          <button
            onClick={() => navigate('/news')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '500', marginBottom: '24px', padding: 0 }}
            onMouseEnter={e => e.currentTarget.style.color = '#0099CC'}
            onMouseLeave={e => e.currentTarget.style.color = '#6B7280'}
          >
            <ArrowLeft size={16} /> Go back
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <span style={{ backgroundColor: '#0099CC', color: 'white', padding: '4px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>{article.category}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px', color: '#9CA3AF' }}><Calendar size={14} />{article.date}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px', color: '#9CA3AF' }}><Clock size={14} />{article.readTime}</span>
          </div>
          <h1 style={{ fontSize: 'clamp(26px, 3.5vw, 42px)', fontWeight: '800', color: '#111827', lineHeight: 1.2, marginBottom: '16px', letterSpacing: '-0.5px' }}>
            {article.title}
          </h1>
          <p style={{ fontSize: '17px', color: '#6B7280', lineHeight: 1.7 }}>{article.excerpt}</p>
        </div>
      </div>

      {/* Cover image */}
      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ borderRadius: '16px', overflow: 'hidden', lineHeight: 0, marginTop: '-20px', boxShadow: '0 8px 40px rgba(0,0,0,0.1)' }}>
          <img
            src={article.image}
            alt={article.title}
            style={{ width: '100%', maxHeight: '480px', objectFit: 'cover', display: 'block' }}
            onError={e => { e.target.src = 'https://images.unsplash.com/photo-1518432031352-d6fc5c10da5a?w=860&q=80'; }}
          />
        </div>
      </div>

      {/* Article body */}
      <article style={{ maxWidth: '860px', margin: '0 auto', padding: '48px 24px 80px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '40px', paddingBottom: '24px', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #BAE6FD' }}>
            <span style={{ fontSize: '16px', fontWeight: '700', color: '#0099CC' }}>{article.author.charAt(0)}</span>
          </div>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>{article.author}</div>
            <div style={{ fontSize: '13px', color: '#9CA3AF' }}>DISION Tech</div>
          </div>
        </div>

        <div style={{ fontSize: '17px', color: '#374151', lineHeight: 1.85 }}>
          {article.content.map((block, i) => {
            if (block.type === 'h2') return <h2 key={i} style={{ fontSize: '26px', fontWeight: '700', color: '#111827', margin: '40px 0 16px', letterSpacing: '-0.3px' }}>{block.text}</h2>;
            if (block.type === 'h3') return <h3 key={i} style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: '28px 0 12px' }}>{block.text}</h3>;
            if (block.type === 'p') return <p key={i} style={{ marginBottom: '20px', lineHeight: 1.85 }}>{block.text}</p>;
            if (block.type === 'quote') return <blockquote key={i} style={{ borderLeft: '4px solid #0099CC', paddingLeft: '20px', margin: '28px 0', color: '#6B7280', fontStyle: 'italic', fontSize: '18px' }}>{block.text}</blockquote>;
            return null;
          })}
        </div>

        {/* Tags */}
        {article.relatedTags && (
          <div style={{ marginTop: '48px', paddingTop: '28px', borderTop: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <Tag size={16} color="#9CA3AF" />
            {article.relatedTags.map(tag => (
              <span key={tag} style={{ padding: '4px 14px', borderRadius: '20px', background: '#F3F4F6', color: '#6B7280', fontSize: '13px', fontWeight: '500' }}>{tag}</span>
            ))}
          </div>
        )}
      </article>

      {/* Related articles */}
      {related.length > 0 && (
        <section style={{ padding: '0 24px 80px', background: 'white' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '26px', fontWeight: '700', color: '#111827', marginBottom: '32px', letterSpacing: '-0.3px' }}>Related articles</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
              {related.map((post, i) => (
                <div
                  key={i}
                  className="project-card"
                  onClick={() => navigate(`/news/${post.slug}`)}
                  style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #E5E7EB', cursor: 'pointer' }}
                >
                  <img src={post.thumbnail} alt={post.title} style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block' }} onError={e => { e.target.src = 'https://images.unsplash.com/photo-1518432031352-d6fc5c10da5a?w=400&q=60'; }} />
                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                      <Calendar size={13} color="#9CA3AF" />
                      <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{post.date}</span>
                    </div>
                    <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#111827', lineHeight: 1.4, marginBottom: '12px' }}>{post.title}</h3>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#0099CC', fontSize: '13px', fontWeight: '600' }}>Read more <ArrowRight size={13} /></span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </PageLayout>
  );
}
